'use client';

import { useState, useEffect } from 'react';
import useSWR from 'swr';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import useFeature from './useFeature';
import useLocale from './useLocale';
import usePreview from '@/components/common/ContentfulPreviewControls/usePreview';
import { TypePromotionCampaign } from '@/core/generated-types';
import { FeatureFlag } from '@/core/featureFlags';

/**
 * Optional override props to skip the promotion fetch and provide values directly.
 * Useful for testing, SSR scenarios, or when parent components want to control the slot.
 */
export interface PromoOverrideProps {
  /** When true, returns null promotion without fetching */
  disabled?: boolean;
  /** Override skeleton display state */
  shouldShowSkeleton?: boolean;
  /** Override promotion data */
  promotion?: TypePromotionCampaign | null;
}

interface UseCurrentPromoReturn {
  promotion: TypePromotionCampaign | null;
  /**
   * True while waiting for the promotion state to be fully resolved.
   * Combines LaunchDarkly initialization and API response status.
   * Use this to avoid flashing fallback content before promotion data arrives.
   */
  isLoading: boolean;
  error: Error | null;
  campaignKey: string | null;
  shouldShowSkeleton: boolean;
}

/**
 * Fetcher function for SWR - fetches promotion campaign from API
 */
const fetchPromotionCampaign = async (
  url: string,
): Promise<TypePromotionCampaign | null> => {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(
      `Failed to fetch promotion campaign: ${response.statusText}`,
    );
  }

  const data = await response.json();
  return data.promotionCampaign;
};

/**
 * Hook to fetch the current active promotion campaign based on feature flag.
 * Uses SWR for caching and request deduplication to prevent unnecessary fetches.
 *
 * @param {PromoOverrideProps} [override] - Optional override props to skip fetch and provide values directly
 * @returns {UseCurrentPromoReturn} Object containing:
 *  - promotion: The promotion campaign data from Contentful (null if feature is off or not found)
 *  - isLoading: Loading state while fetching from Contentful
 *  - error: Any error that occurred during fetch
 *  - campaignKey: The current campaign key from the feature flag
 *  - shouldShowSkeleton: Whether to show skeleton loader (true when feature is on but data not yet loaded)
 *
 * @example
 * // Default usage - fetches from API
 * const { promotion, shouldShowSkeleton } = useCurrentPromo();
 *
 * @example
 * // Disable the slot without fetching
 * const { promotion } = useCurrentPromo({ disabled: true });
 *
 * @example
 * // Override with specific promotion data (skips fetch)
 * const { promotion } = useCurrentPromo({ promotion: myPromoData });
 */
export default function useCurrentPromo(
  override?: PromoOverrideProps,
): UseCurrentPromoReturn {
  const locale = useLocale();
  const ldClient = useLDClient();
  const campaignKey = useFeature(FeatureFlag.MONEY_PROMOTION_CAMPAIGN, 'off');
  const { isPreview } = usePreview();

  // Track LaunchDarkly initialization state
  const [isLDReady, setIsLDReady] = useState(false);

  useEffect(() => {
    if (!ldClient) {
      setIsLDReady(false);
      return;
    }

    // Wait for LD to initialize, then mark as ready
    ldClient
      .waitForInitialization()
      .then(() => setIsLDReady(true))
      .catch(() => setIsLDReady(true)); // Mark ready even on error to avoid infinite loading
  }, [ldClient]);

  // Check if override props bypass the fetch
  const hasOverride =
    override?.disabled ||
    override?.shouldShowSkeleton !== undefined ||
    override?.promotion !== undefined;

  // Only construct URL and fetch if feature flag is enabled AND no override provided
  const shouldFetch = !hasOverride && campaignKey && campaignKey !== 'off';
  const url = shouldFetch
    ? `/api/contentful/promotionCampaign?campaignKey=${encodeURIComponent(campaignKey)}&locale=${encodeURIComponent(locale)}${isPreview ? '&preview=true' : ''}`
    : null;

  // Use SWR for caching and request deduplication
  // - Multiple components calling this hook will share the same request
  // - Results are cached to avoid refetching on remounts
  // - Automatic revalidation on focus/reconnect can be configured
  const {
    data: promotion,
    error,
    isLoading,
  } = useSWR(
    url, // null when feature is off or override provided, which prevents fetching
    fetchPromotionCampaign,
    {
      revalidateOnFocus: false, // Don't refetch when window regains focus
      revalidateOnReconnect: false, // Don't refetch on reconnect
      dedupingInterval: 60000, // Dedupe requests within 60 seconds
      errorRetryCount: 1, // Only retry once on error
    },
  );

  // Track if we've received the promotion API response for the CURRENT url
  // This helps avoid flash when switching from no-fetch to fetch state
  const [hasReceivedResponse, setHasReceivedResponse] = useState(false);
  const [trackedUrl, setTrackedUrl] = useState<string | null | undefined>(
    undefined,
  );

  useEffect(() => {
    // If URL changed (or first run), update tracking
    if (url !== trackedUrl) {
      setTrackedUrl(url);
      // If no URL (not fetching), consider immediately ready
      // Otherwise, wait for the fetch to complete
      setHasReceivedResponse(!url);
      return;
    }

    // If we have a URL and loading finished, we've received the response
    if (url && !isLoading) {
      setHasReceivedResponse(true);
    }
  }, [url, isLoading, trackedUrl]);

  // Return override values if disabled
  if (override?.disabled) {
    return {
      promotion: null,
      isLoading: false, // Always ready when disabled
      error: null,
      campaignKey: null,
      shouldShowSkeleton: false,
    };
  }

  // Return override values if provided
  if (hasOverride) {
    return {
      promotion: override.promotion ?? null,
      isLoading: false, // Always ready when override provided
      error: null,
      campaignKey: campaignKey === 'off' ? null : campaignKey,
      shouldShowSkeleton: override.shouldShowSkeleton ?? false,
    };
  }

  // Ready when LD has initialized AND we've received the API response (or don't need to fetch)
  const isReady = isLDReady && hasReceivedResponse;

  // Show skeleton when we're expecting promotion data but not ready yet
  const shouldShowSkeleton =
    !isReady && campaignKey !== null && campaignKey !== 'off';

  return {
    promotion: promotion ?? null,
    isLoading: !isReady, // True while waiting for LD + API to resolve
    error: error ?? null,
    campaignKey: campaignKey === 'off' ? null : campaignKey,
    shouldShowSkeleton,
  };
}
